import React from "react"
import { accessFromProps } from "../lib/book-access"
import { canAccessModule } from "../lib/book-nav"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return "₹0.00"
  return `₹${(Number(cents) / 100).toFixed(2)}`
}

export function DashboardPage(props) {
  const {
    user_name,
    dashboard = {},
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)
  const setupCompany = access?.needs_company && access?.can_create_company
  const showReceivables = canAccessModule(access, "sales")
  const showPayables = canAccessModule(access, "purchases")
  const showProjects = canAccessModule(access, "time-tracking")
  const showBanking = canAccessModule(access, "banking")

  return (
    <BookShell activeSlug="home" userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Dashboard"
          actions={<span className="text-sm text-muted-foreground">This fiscal year</span>}
        />

        {setupCompany ? (
          <Card className="border-primary/40 bg-primary/[0.06]">
            <CardHeader>
              <CardTitle>Register your company</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Actinium Book needs a company (organization) before you can record vendors, customers, invoices, and journals. Use the form
                on the next screen—takes under a minute.
              </p>
              <a
                href="/admin"
                className="inline-flex h-9 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 sm:w-auto"
              >
                Continue to company setup
              </a>
            </CardContent>
          </Card>
        ) : null}

        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-5">
          {showReceivables ? (
          <Card className="flex h-full min-h-0 flex-col">
            <CardHeader>
              <CardTitle>Total Receivables</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col">
              <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{centsToAmount(dashboard.total_receivables_cents)}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Current: {centsToAmount(dashboard.current_receivables_cents)} · Overdue: {centsToAmount(dashboard.overdue_receivables_cents)}
              </p>
              {dashboard.pending_approvals_count > 0 ? (
                <p className="mt-1 text-xs text-amber-600">{dashboard.pending_approvals_count} pending approval(s)</p>
              ) : null}
            </CardContent>
          </Card>
          ) : null}
          {showPayables ? (
          <Card className="flex h-full min-h-0 flex-col">
            <CardHeader>
              <CardTitle>Total Payables</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col">
              <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{centsToAmount(dashboard.total_payables_cents)}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Current: {centsToAmount(dashboard.current_payables_cents)} · Overdue: {centsToAmount(dashboard.overdue_payables_cents)}
              </p>
            </CardContent>
          </Card>
          ) : null}
          {showProjects ? (
          <Card className="flex h-full min-h-0 flex-col">
            <CardHeader>
              <CardTitle>Projects</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col">
              <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{dashboard.active_projects_count || 0}</p>
              <p className="mt-2 text-sm text-muted-foreground">Active project(s)</p>
            </CardContent>
          </Card>
          ) : null}
          {showBanking ? (
          <Card className="flex h-full min-h-0 flex-col">
            <CardHeader>
              <CardTitle>Bank and credit cards</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col">
              <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{centsToAmount(dashboard.cash_balance_cents)}</p>
              <p className="mt-2 text-sm text-muted-foreground">{dashboard.bank_accounts_count || 0} account(s) connected</p>
            </CardContent>
          </Card>
          ) : null}
        </section>

        {(showReceivables && dashboard.recent_invoices?.length > 0) || (showPayables && dashboard.recent_bills?.length > 0) ? (
          <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-5">
            {showReceivables && dashboard.recent_invoices?.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-1 pr-2">#</th>
                          <th className="py-1 pr-2">Customer</th>
                          <th className="py-1 pr-2 text-right">Amount</th>
                          <th className="py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.recent_invoices.map((inv) => (
                          <tr key={inv.id} className="border-b border-border/40">
                            <td className="py-1 pr-2">{inv.number}</td>
                            <td className="py-1 pr-2">{inv.customer || "—"}</td>
                            <td className="py-1 pr-2 text-right">{centsToAmount(inv.total_cents)}</td>
                            <td className="py-1 capitalize">{inv.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : null}
            {showPayables && dashboard.recent_bills?.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Bills</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-1 pr-2">#</th>
                          <th className="py-1 pr-2">Vendor</th>
                          <th className="py-1 pr-2 text-right">Amount</th>
                          <th className="py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.recent_bills.map((bill) => (
                          <tr key={bill.id} className="border-b border-border/40">
                            <td className="py-1 pr-2">{bill.number}</td>
                            <td className="py-1 pr-2">{bill.vendor || "—"}</td>
                            <td className="py-1 pr-2 text-right">{centsToAmount(bill.total_cents)}</td>
                            <td className="py-1 capitalize">{bill.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </section>
        ) : null}
      </div>
    </BookShell>
  )
}
